import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, CreditCard, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const currencies = [
  { code: 'INR', symbol: '₹', rate: 83 },
  { code: 'USD', symbol: '$', rate: 1 },
  { code: 'EUR', symbol: '€', rate: 0.92 },
  { code: 'GBP', symbol: '£', rate: 0.79 },
];

const tiers = [
  {
    name: 'Free',
    description: 'Perfect for getting started',
    features: [
      '3 video conversions per month',
      'Basic notes generation',
      'Preview thumbnails',
      'Sample downloads',
      'Community support',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    name: 'Premium',
    price: { monthly: 12, annual: 96 },
    description: 'Everything you need to succeed',
    features: [
      'Unlimited video conversions',
      'Advanced AI-powered notes',
      'Custom mind maps',
      'MCQ generation',
      'Flashcards',
      'Priority support',
      'Team collaboration',
      'Export in any format',
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
];

export function PricingTiers() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [currency, setCurrency] = useState(currencies[0]); // INR is default

  const formatPrice = (price: number) => {
    const converted = price * currency.rate;
    return `${currency.symbol}${Math.round(converted)}`;
  };

  return (
    <div className="mt-8">
      <div className="mb-8 flex items-center justify-center gap-4">
        <select
          value={currency.code}
          onChange={(e) => {
            const selected = currencies.find((c) => c.code === e.target.value);
            if (selected) setCurrency(selected);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        >
          {currencies.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-8 md:grid-cols-2 md:gap-12">
        {tiers.map((tier, index) => (
          <motion.div
            key={tier.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={cn(
              'relative rounded-2xl border bg-white p-8 shadow-sm transition-all hover:shadow-lg',
              {
                'border-indigo-200 ring-2 ring-indigo-500': tier.popular,
                'border-gray-200': !tier.popular,
              }
            )}
          >
            {tier.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-1 text-sm font-medium text-white">
                Most Popular
              </div>
            )}

            <div className="mb-8">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-gray-900">
                  {tier.name}
                </h3>
                {tier.popular && (
                  <GraduationCap className="h-5 w-5 text-indigo-500" />
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">{tier.description}</p>
              {'price' in tier && (
                <div className="mt-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(
                        isAnnual ? tier.price.annual / 12 : tier.price.monthly
                      )}
                    </span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  {isAnnual && (
                    <p className="mt-1 text-sm text-gray-500">
                      Billed annually ({formatPrice(tier.price.annual)}/year)
                    </p>
                  )}
                </div>
              )}
            </div>

            <ul className="mb-8 space-y-4">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm">
                  <Check className="h-5 w-5 text-indigo-500" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              variant={tier.popular ? 'primary' : 'outline'}
              size="lg"
              className="w-full"
            >
              {tier.cta}
            </Button>

            {tier.popular && (
              <div className="mt-4">
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  {['Visa', 'Mastercard', 'PayPal'].map((method) => (
                    <span
                      key={method}
                      className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}